<template lang="html">
  <section class="drag-drop-page">
    <div class="left-panel">
      <create-component
        :id="`dd_${item.name}`"
        v-for="item in draggable"
        :key="item.id"
        @drag-start="onCreateStartDrag"
        @drag-end="onCreateEndDrag"
        :icon="item.icon"
        :label="item.label"
        :message-key="item.messageKey"
        :modal="item.modal"
        :drop-effect="item.dropEffect"
        :props="item.props"
      />
      <div class="channels-list">
        <distribution-channel-list
          @on-drag-start="onCreateStartDrag"
          @on-drag-end="onCreateEndDrag"
        />
      </div>
    </div>

    <div class="right-panel" ref="canvas"></div>
    <div ref="modals"></div>
  </section>
</template>

<script lang="js">
  import { mapState, mapGetters, mapActions } from "vuex";
  import { MUTATION_NAME } from "@/static/global-variables";
  import debounce from 'lodash/debounce';
  import isEqual from "lodash/isEqual";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import canvasUtil from "@/views/drag-drop-pages/components/utils/canvas-util";

  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import { DRAGGABLE } from "@/views/drag-drop-pages/static/draggable";
  import CreateComponent from "@/views/drag-drop-pages/components/builder/CreateComponent";
  import DistributionChannelList from "@/components/custom-display-elements/page-elements/DistributionChannelList";

  import AddCardModal from "@/views/drag-drop-pages/components/builder/modal/AddCardModal";
  import CardLayout from "@/views/drag-drop-pages/components/CardLayout";
  import AddTableModal from "@/views/drag-drop-pages/components/builder/modal/AddTableModal";
  import TableComponent from "@/views/drag-drop-pages/components/TableComponent";
  import AddFieldListModal from "@/views/drag-drop-pages/components/builder/modal/AddFieldListModal";
  import FieldList from "@/views/drag-drop-pages/components/FieldList";
  import AddProgressChartModal from "@/views/drag-drop-pages/components/builder/modal/AddProgressChartModal";
  import ProgressChart from "@/views/drag-drop-pages/components/ProgressChart";
  import AddTrendChartModal from "@/views/drag-drop-pages/components/builder/modal/AddTrendChartModal";
  import TrendChart from "@/views/drag-drop-pages/components/TrendChart";
  import AddMetricModal from "@/views/drag-drop-pages/components/builder/modal/AddMetricModal";
  import MetricHighlight from "@/views/drag-drop-pages/components/MetricHighlight";
  import AddDropdownModal from "@/views/drag-drop-pages/components/builder/modal/AddDropdownModal";
  import Dropdown from "@/views/drag-drop-pages/components/Dropdown";
  import AddDatepickerModal from "@/views/drag-drop-pages/components/builder/modal/AddDatepickerModal";
  import Datepicker from "@/views/drag-drop-pages/components/Datepicker";

  import CustomDisplayElementConfig from "@/components/custom-display-elements/page-elements/CustomDisplayElementConfig";
  import AddEditCustomDisplayElementModal from "@/components/custom-display-elements/modal/AddEditCustomDisplayElementModal";

  export default  {
    name: "drag-drop-page",
    components: {
     CreateComponent,
     DistributionChannelList
    },
    props: {
      appId: {
        type: String
      },
      pageName: {
        type: String
      }
    },
    watch: {
      apps: function(newVal, oldVal) {
        if(!isEqual(newVal, oldVal)) {
          this.generateElements();
        }
      },
      updateDraggableItem() {
        this.onCanvasUpdate(this.updateDraggableItem);
      },
      moveDraggableItem() {
        this.generateElements();
      },
      pageName: function() {
        this.generateElements();
      }
    },
    computed: {
      ...mapState({
        apps: "apps"
      }),
      ...mapGetters([
        "updateDraggableItem",
        "moveDraggableItem"
      ]),
      content() {
        return JSON.parse(JSON.stringify(expressionBuilder.getAppContent(this.appId)));
      }
    },
    mounted () {
      this.mainLayout = "main-layout";
      dragAndDrop.addCanvasRef(this, this.pageName);
      dragAndDrop.setAppId(this.appId);
      // set modals and components that will be created programmatically
      dragAndDrop.setComponets({
        AddCardModal, CardLayout,
        AddTableModal, TableComponent,
        AddFieldListModal, FieldList,
        AddProgressChartModal, ProgressChart,
        AddTrendChartModal, TrendChart,
        AddMetricModal, MetricHighlight,
        AddDropdownModal, Dropdown,
        AddDatepickerModal, Datepicker,
        AddEditCustomDisplayElementModal, CustomDisplayElementConfig
      });
      this.generateElements();
    },
    data () {
      return {
        draggable: DRAGGABLE
      }
    },
    methods: {
      ...mapActions({
        setIsDirtyFlag: MUTATION_NAME.UPDATE_IS_DIRTY_FLAG
      }),
      onCanvasUpdate: debounce(function(data) {
        this.$refs[this.mainLayout].normalizeElements();
        dragAndDrop.savePageMetadata(this.appId, this.pageName, data.refeshCanvas);
      }, 500),
      generateElements() {
        canvasUtil.reset();
        const oldLayout = this.$refs[this.mainLayout];
        if(oldLayout) {
          this.$refs.canvas.removeChild(oldLayout.$el);
          oldLayout.$destroy();
          delete this.$refs[this.mainLayout];
        }
        const data = {
          props: {
            id: this.mainLayout,
            card: false
          }
        };
        this.addComponent("CardLayout", data, data.props.card);

        if (this.content && this.content.pages) {
          const page = this.content.pages.find(el => el.name === this.pageName);
          if (page && page.display && page.display.element) {
            canvasUtil.iniCanvas(this, page, this.appId, this.mainLayout);
          }
        }
      },
      showDropzoneLines() {
        setTimeout(() => {
          canvasUtil.displayDropzoneLines(this, this.mainLayout);
        }, 50);
      },
      removeDropzoneLines() {
        setTimeout(() => {
          canvasUtil.hideDropzoneLines(this.$refs[this.mainLayout]);
        }, 50);
      },
      onCreateStartDrag(obj) {
        obj.ev.dataTransfer.effectAllowed = obj.dropEffect;
        dragAndDrop.setCreateElementData(obj.data);
        this.showDropzoneLines();
      },
      onCreateEndDrag() {
        this.removeDropzoneLines();
      },
      onComponentDropzone(ev, refComponent) {
        const moveComponentId = ev.dataTransfer.getData("component.move");
        if(moveComponentId) {
          this.moveComponent(moveComponentId, refComponent);
        } else {
          const obj = dragAndDrop.getCreateElementData();
          this.showAddModal(obj.modal, obj.props);
        }
      },
      showAddModal(modal, initialProps) {
        const instance = dragAndDrop.createModal(modal, this.toggleModal, this.onModalDone);
        if(instance) {
          if(initialProps) {
            for (const [key, value] of Object.entries(initialProps)) {
              instance[key] = value;
            }
          }
          this.$refs.modals.appendChild(instance.$el);
          this.$refs[instance.id] = instance;
        } else {
          console.error(`Modal component for ${modal} not added to the list`);
        }
      },
      removeModal(id) {
        if(id && this.$refs[id]) {
          this.$refs[id].show = false;
          const oldInstance = this.$refs[id];
          this.$refs.modals.removeChild(oldInstance.$el);
          delete this.$refs[id];
        }
      },
      toggleModal(obj) {
        this.removeModal(obj.id);
      },
      onModalDone(obj) {
        this.setIsDirtyFlag({appId: this.appId, isDirty: true});
        this.removeModal(obj.modal.id);
        this.addComponent(obj.modal.create, obj);
      },

      onComponentStartDrag() {},
      onComponentEndDrag() {},

      onChildComponentStartDragging(ctx) {
        this.moveInstance = ctx;
        this.showDropzoneLines();
      },
      onChildComponentEndDragging() {
        this.removeDropzoneLines();
      },
      addComponent(create, data, isDraggable) {
        const instance = dragAndDrop.createDraggableComponent(create, data, isDraggable, this.onComponentStartDrag, this.onComponentEndDrag);
        if(instance) {
          canvasUtil.appendComponent(instance, this);
        } else {
          console.error(`Component for ${create} not added to the list`);
        }
      },

      moveComponent(id, refComponent) {
        if(this.moveInstance.id === id) {
          canvasUtil.moveComponent(this, this.mainLayout, this.moveInstance, refComponent);
        }
      },

      /**
       * @function getMetadata
       * @returns {Object} Metadata in JSON format
      */
      getMetadata() {
        if(!this.$refs[this.mainLayout]) {
           return;
        }
        const component = this.$refs[this.mainLayout];
        if(component.$refs.canvas.childNodes.length === 0) {
          // empty canvas, no need to return empty object
          return;
        }
        const meta = component.getMetadata();
        if(meta.type === "Section") {
          return {
            name: this.pageName,
            display: meta.body
          };
        } else {
          return {
            name: this.pageName,
            display: meta
          };
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  .drag-drop-page {
    display: grid;
    grid-template-columns: 320px auto;
    min-height: 100%;
    width: 100%;
    box-sizing: border-box;
    font-size: 0.8rem;
    .left-panel {
      max-height: 80vh;
      overflow-y: auto;
      box-sizing: border-box;
      border: 1px solid #f0f5f5;
      display: flex;
      flex-wrap: wrap;
      align-content: flex-start;
      padding-top: 0.5rem;
      padding-left: 0.5rem;
      .channels-list {
        width: 100%;
        padding-top: 1.5rem;
        padding-left: 0.3rem;
      }
    }
    .right-panel {
      max-height: 80vh;
      box-sizing: border-box;
      padding: 20px;
      position: relative;
      overflow: auto;
    }
    .load-canvas-container {
      position: absolute;
      background-color: rgba(255, 255, 255, 0.1);
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
</style>
